import React, { useState, useEffect } from 'react';
import { a2aAdminService } from '../../a2a/services/a2aAdminService.js';
import './SystemSettingsModule.css';

const SystemSettingsModule = ({ currentUser, onBack, onTabChange }) => {
  const [settings, setSettings] = useState({});
  const [activeTab, setActiveTab] = useState('general');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    setLoading(true);
    setError('');
    try {
      const tenantId = currentUser?.tenant_id || 'tn_01';
      const result = await a2aAdminService.getSystemSettings({
        tenant_id: tenantId
      });
      if (result.success) {
        setSettings(result.data || {});
      } else {
        setError(result.error || 'Failed to fetch settings');
      }
    } catch (err) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (tabName, updatedSettings) => {
    setSaving(true);
    setError('');
    try {
      // TODO: Implement save settings
      console.log('Saving settings for', tabName, updatedSettings);
      alert('Save settings functionality coming soon');
      setSettings({ ...settings, [tabName]: updatedSettings });
    } catch (err) {
      setError(err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="system-settings-module">
      <div className="module-header">
        <button className="back-button" onClick={onBack}>← 返回</button>
        <h2>系統設定</h2>
      </div>
      
      {error && <div className="error-message">{error}</div>}
      
      {loading ? (
        <div className="loading">載入中...</div>
      ) : (
        <>
          <div className="settings-tabs">
            <button 
              className={activeTab === 'general' ? 'active' : ''} 
              onClick={() => setActiveTab('general')}
            >
              一般設定
            </button>
            <button 
              className={activeTab === 'security' ? 'active' : ''} 
              onClick={() => setActiveTab('security')}
            >
              安全設定
            </button>
            <button 
              className={activeTab === 'notifications' ? 'active' : ''} 
              onClick={() => setActiveTab('notifications')}
            >
              通知設定
            </button>
            <button 
              className={activeTab === 'integrations' ? 'active' : ''} 
              onClick={() => setActiveTab('integrations')}
            >
              整合設定
            </button>
          </div>
          
          <div className="settings-content">
            {activeTab === 'general' && (
              <GeneralSettings 
                settings={settings.general || {}} 
                onSave={(data) => handleSave('general', data)}
                saving={saving}
              />
            )}
            {activeTab === 'security' && (
              <SecuritySettings 
                settings={settings.security || {}} 
                onSave={(data) => handleSave('security', data)}
                saving={saving}
              />
            )}
            {activeTab === 'notifications' && (
              <NotificationSettings 
                settings={settings.notifications || {}} 
                onSave={(data) => handleSave('notifications', data)}
                saving={saving}
              />
            )}
            {activeTab === 'integrations' && (
              <IntegrationSettings 
                settings={settings.integrations || {}} 
                onSave={(data) => handleSave('integrations', data)}
                saving={saving}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
};

// General Settings Component
const GeneralSettings = ({ settings, onSave, saving }) => {
  const [formData, setFormData] = useState({
    school_name: settings.school_name || '',
    language: settings.language || 'zh-TW',
    timezone: settings.timezone || 'Asia/Hong_Kong'
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="settings-form">
      <div className="form-group">
        <label>學校名稱</label>
        <input
          type="text"
          value={formData.school_name}
          onChange={(e) => setFormData({...formData, school_name: e.target.value})}
        />
      </div>
      <div className="form-group">
        <label>語言</label>
        <select
          value={formData.language}
          onChange={(e) => setFormData({...formData, language: e.target.value})}
        >
          <option value="zh-TW">繁體中文</option>
          <option value="zh-CN">简体中文</option>
          <option value="en-US">English</option>
        </select>
      </div>
      <div className="form-group">
        <label>時區</label>
        <select
          value={formData.timezone}
          onChange={(e) => setFormData({...formData, timezone: e.target.value})}
        >
          <option value="Asia/Hong_Kong">Asia/Hong_Kong (UTC+8)</option>
          <option value="UTC">UTC</option>
        </select>
      </div>
      <div className="form-actions">
        <button type="submit" disabled={saving}>
          {saving ? '儲存中...' : '儲存'}
        </button>
      </div>
    </form>
  );
};

// Security Settings Component
const SecuritySettings = ({ settings, onSave, saving }) => {
  const [formData, setFormData] = useState({
    password_policy: settings.password_policy || 'standard',
    session_timeout: settings.session_timeout || 3600,
    two_factor_enabled: settings.two_factor_enabled || false
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="settings-form">
      <div className="form-group">
        <label>密碼政策</label>
        <select
          value={formData.password_policy}
          onChange={(e) => setFormData({...formData, password_policy: e.target.value})}
        >
          <option value="standard">標準</option>
          <option value="strong">強制</option>
          <option value="custom">自訂</option>
        </select>
      </div>
      <div className="form-group">
        <label>會話超時 (秒)</label>
        <input
          type="number"
          value={formData.session_timeout}
          onChange={(e) => setFormData({...formData, session_timeout: parseInt(e.target.value)})}
          min="300"
          max="86400"
        />
      </div>
      <div className="form-group">
        <label>
          <input
            type="checkbox"
            checked={formData.two_factor_enabled}
            onChange={(e) => setFormData({...formData, two_factor_enabled: e.target.checked})}
          />
          啟用雙因素認證
        </label>
      </div>
      <div className="form-actions">
        <button type="submit" disabled={saving}>
          {saving ? '儲存中...' : '儲存'}
        </button>
      </div>
    </form>
  );
};

// Notification Settings Component
const NotificationSettings = ({ settings, onSave, saving }) => {
  const [formData, setFormData] = useState({
    email_enabled: settings.email_enabled !== false,
    sms_enabled: settings.sms_enabled || false,
    push_enabled: settings.push_enabled !== false
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="settings-form">
      <div className="form-group">
        <label>
          <input
            type="checkbox"
            checked={formData.email_enabled}
            onChange={(e) => setFormData({...formData, email_enabled: e.target.checked})}
          />
          啟用電郵通知
        </label>
      </div>
      <div className="form-group">
        <label>
          <input
            type="checkbox"
            checked={formData.sms_enabled}
            onChange={(e) => setFormData({...formData, sms_enabled: e.target.checked})}
          />
          啟用SMS通知
        </label>
      </div>
      <div className="form-group">
        <label>
          <input
            type="checkbox"
            checked={formData.push_enabled}
            onChange={(e) => setFormData({...formData, push_enabled: e.target.checked})}
          />
          啟用推送通知
        </label>
      </div>
      <div className="form-actions">
        <button type="submit" disabled={saving}>
          {saving ? '儲存中...' : '儲存'}
        </button>
      </div>
    </form>
  );
};

// Integration Settings Component
const IntegrationSettings = ({ settings, onSave, saving }) => {
  const [formData, setFormData] = useState({
    api_enabled: settings.api_enabled !== false,
    webhook_url: settings.webhook_url || ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="settings-form">
      <div className="form-group">
        <label>
          <input
            type="checkbox"
            checked={formData.api_enabled}
            onChange={(e) => setFormData({...formData, api_enabled: e.target.checked})}
          />
          啟用API
        </label>
      </div>
      <div className="form-group">
        <label>Webhook URL</label>
        <input
          type="url"
          value={formData.webhook_url}
          onChange={(e) => setFormData({...formData, webhook_url: e.target.value})}
          placeholder="https://example.com/webhook"
        />
      </div>
      <div className="form-actions">
        <button type="submit" disabled={saving}>
          {saving ? '儲存中...' : '儲存'}
        </button>
      </div>
    </form>
  );
};

export default SystemSettingsModule;

